import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'

import { SLIDE_CAP } from '../constants'
import { presentationEditPath } from '../featureFlag'
import * as presentationService from '../services/presentationService'
import type { Story } from '../types/presentation.types'
import { emptyDeck, newId } from '../utils/deckOps'
import { DeckTargetField, type DeckTarget } from './DeckTargetField'

/**
 * Put the Explorer grid on a slide (MYD-506): an `explorer.table` block
 * (MYD-504), in a story of its own, in a new deck or one of the user's own.
 *
 * It carries the grid's DEFINITION, never its rows: the slide re-runs the
 * query as whoever views it. A saved report with no unsaved changes goes by
 * its id, so editing it in Explorer later updates the slide; anything else
 * goes inline, as it is on screen now.
 */

interface AddToPresentationDialogProps {
	open: boolean
	onClose: () => void
	/** The explorer.table config: `{ savedReportId }` or the inline definition. */
	tableConfig: Record<string, unknown>
	/** The report's name, or the grain's label. */
	title: string
	/** Said in the dialog, so the user knows whether later edits will follow. */
	linkedToReport: boolean
}

/** A story holding one slide with the table on it. */
export const tableStory = (title: string, config: Record<string, unknown>): Story => ({
	id: newId(),
	title,
	anchor: null,
	divider: false,
	slides: [
		{
			id: newId(),
			layout: 'title-body',
			title,
			subtitle: '',
			speakerNotes: '',
			transition: null,
			blocks: [{ id: newId(), region: 'main', element: 'explorer.table', source: null, config }],
		},
	],
})

export function AddToPresentationDialog({ open, onClose, tableConfig, title, linkedToReport }: AddToPresentationDialogProps) {
	const navigate = useNavigate()
	const [slideTitle, setSlideTitle] = useState(title)
	const [target, setTarget] = useState<DeckTarget | null>(null)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		if (!open) return
		setSlideTitle(title)
		setError(null)
	}, [open, title])

	const full = target?.kind === 'existing' && target.slideCount >= SLIDE_CAP

	const add = async () => {
		if (!target || full) return
		setBusy(true)
		setError(null)
		const story = tableStory(slideTitle.trim() || title, tableConfig)
		try {
			let deckId: string
			if (target.kind === 'new') {
				deckId = (await presentationService.createPresentation({ name: target.name, definition: { ...emptyDeck(), stories: [story] } })).id
			} else {
				// Read, append, write: the server re-validates the whole deck.
				const deck = await presentationService.getPresentation(target.id)
				await presentationService.updatePresentation(target.id, { definition: { ...deck.definition, stories: [...deck.definition.stories, story] } })
				deckId = target.id
			}
			navigate(presentationEditPath(deckId))
		} catch (cause: any) {
			setError(cause?.response?.data?.message ?? 'The table could not be added.')
		} finally {
			setBusy(false)
		}
	}

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>Add to presentation</DialogTitle>
					<DialogDescription>
						This table, on a slide of its own. Each viewer sees only the rows they can access.{' '}
						{linkedToReport ? 'It follows the saved report: edit the report and the slide changes.' : 'It is the table as it is now; save it as a report to keep the two in step.'}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 px-5 py-4">
					<div className="space-y-1.5">
						<Label htmlFor="add-table-title">Slide title</Label>
						<Input id="add-table-title" value={slideTitle} maxLength={200} onChange={event => setSlideTitle(event.target.value)} />
					</div>
					<DeckTargetField open={open} defaultName={title} onChange={setTarget} />
					{full && <p className="text-xs text-destructive">That presentation already holds {SLIDE_CAP} slides, the most it can.</p>}
					{error && <p className="text-sm text-destructive">{error}</p>}
				</div>

				<DialogFooter>
					<Button variant="ghost" isOutline onClick={onClose} disabled={busy}>
						Cancel
					</Button>
					<Button onClick={add} disabled={busy || !target || full}>
						{busy ? 'Adding...' : 'Add slide'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default AddToPresentationDialog
